import dayjs from 'dayjs';

import { createActionEvent } from '@/api/audit';
import { createActionEventId, setActiveActionEvent } from '@/utils/audit-context';


// 覆盖原生按钮、链接和 TDesign 中常用的交互组件。
const ACTION_ELEMENT_SELECTOR = [
  'button',
  'a[href]',
  'input[type="button"]',
  'input[type="submit"]',
  'input[type="checkbox"]',
  'input[type="radio"]',
  '[data-audit-key]',
  '[role="button"]',
  '[role="menuitem"]',
  '[role="option"]',
  '.t-button',
  '.t-switch',
  '.t-checkbox',
  '.t-radio',
  '.t-menu__item',
  '.t-dropdown__item',
  '.t-select-option',
  '.t-tabs__nav-item',
  '.t-pagination__number'
].join(',');

// 清理按钮文字，避免空格和换行影响按钮名称。
const normalizeText = (value, maxLength = 150) => {
  return String(value || '').replace(/\s+/g, ' ').trim().slice(0, maxLength);
};

// 为中文按钮生成稳定的 ASCII 编码，避免中文写入 HTTP 请求头导致请求失败。
const createActionKey = (pageKey, actionIdentity, customActionKey) => {
  const originalKey = normalizeText(customActionKey || `${pageKey}.${actionIdentity}`);
  const asciiKey = originalKey.replace(/\s+/g, '_');

  if (/^[A-Za-z0-9._:-]+$/.test(asciiKey)) {
    return asciiKey.slice(0, 150);
  }

  // 使用简单稳定哈希区分同一页面上的不同中文操作。
  let hash = 2166136261;
  for (let index = 0; index < originalKey.length; index += 1) {
    hash ^= originalKey.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  const safePageKey = String(pageKey || 'page')
    .replace(/[^A-Za-z0-9._:-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '') || 'page';

  return `${safePageKey}.action_${(hash >>> 0).toString(16)}`.slice(0, 150);
};

// 获取用户能看到的按钮名称，图标按钮优先读取配置的名称。
const getActionName = (element) => {
  return normalizeText(
    element.dataset.auditName ||
    element.getAttribute('aria-label') ||
    element.getAttribute('title') ||
    element.value ||
    element.textContent ||
    element.getAttribute('name') ||
    element.id ||
    '未命名操作'
  );
};

// data-audit-context 可按需传 JSON，未配置时只记录基础元素信息。
const getActionContext = (element) => {
  let customContext = {};

  if (element.dataset.auditContext) {
    try {
      customContext = JSON.parse(element.dataset.auditContext);
    } catch (error) {
      customContext = {
        audit_context: element.dataset.auditContext
      };
    }
  }

  return {
    ...customContext,
    element_id: element.id || undefined,
    element_name: element.getAttribute('name') || undefined,
    element_type: element.getAttribute('type') || element.tagName.toLowerCase()
  };
};

// 全局监听用户操作；关键按钮可使用 data-audit-key 固定操作编码。
export const setupActionAudit = (router) => {
  document.addEventListener('click', (event) => {
    const element = event.target?.closest?.(ACTION_ELEMENT_SELECTOR);

    if (!element || !localStorage.getItem('access_token')) {
      return;
    }

    // 禁用组件和明确排除的组件不记录点击。
    if (
      element.disabled ||
      element.getAttribute('aria-disabled') === 'true' ||
      element.classList.contains('t-is-disabled') ||
      element.dataset.auditIgnore === 'true'
    ) {
      return;
    }

    const currentRoute = router.currentRoute.value;
    const pageKey = normalizeText(currentRoute.name || currentRoute.path || 'unknown-page');
    const actionName = getActionName(element);
    const actionIdentity = normalizeText(
      element.dataset.auditKey || element.getAttribute('name') || element.id || actionName
    );
    const actionKey = createActionKey(
      pageKey,
      actionIdentity,
      element.dataset.auditKey
    );
    const eventId = createActionEventId();

    // 先保存上下文，当前点击同步触发的业务请求才能带上关联请求头。
    setActiveActionEvent(eventId, actionKey);

    const actionData = {
      event_id: eventId,
      action_key: actionKey,
      action_name: actionName,
      page_key: pageKey,
      route_path: currentRoute.fullPath,
      component_name: normalizeText(element.dataset.auditComponent || pageKey),
      context_data: getActionContext(element),
      // 后端关闭了 USE_TZ，直接传浏览器本地时间，避免携带 Z 时区。
      client_time: dayjs().format('YYYY-MM-DD HH:mm:ss.SSS')
    };

    // 埋点失败不阻断用户当前操作，后端接口审计仍会正常记录业务请求。
    createActionEvent(actionData).catch(() => {});
  }, true);
};
